// TenantDatabases API – konfiguracja baz tenanta (Storage)

import request from './index';
import type {
  ITenantDatabase,
  ITenantDatabaseResponse,
  ITenantDatabaseForm,
  IVerifyResponse,
  IMigrateResponse
} from '../types/tenantDatabase';

/** Backend (ResponseBuilder) zwraca { data: ... }; normalizujemy do { tenant_database } */
const normalizeTenantDatabaseResponse = (res: unknown): ITenantDatabaseResponse => {
  const r = res as Record<string, unknown>;
  if (r?.tenant_database != null) return r as unknown as ITenantDatabaseResponse;
  if (r?.data != null) return { tenant_database: r.data as ITenantDatabase };
  return res as unknown as ITenantDatabaseResponse;
};

/** Jedna równoległa request w toku per klucz (unikamy podwójnego fetchu przy Strict Mode / podwójnym mount). */
const getTenantDatabaseCache: Record<string, Promise<ITenantDatabaseResponse>> = {};

export const getTenantDatabase = async (id: string): Promise<ITenantDatabaseResponse> => {
  const cacheKey = id;
  const cached = getTenantDatabaseCache[cacheKey];
  if (cached !== undefined) {
    return cached;
  }
  const promise = request<unknown>({
    options: {
      url: `/tenant_databases/${id}`,
      method: 'get'
    }
  })
    .then(res => {
      delete getTenantDatabaseCache[cacheKey];
      return normalizeTenantDatabaseResponse(res);
    })
    .catch(err => {
      delete getTenantDatabaseCache[cacheKey];
      throw err;
    });
  getTenantDatabaseCache[cacheKey] = promise;
  return promise;
};

export const createTenantDatabase = async (
  data: ITenantDatabaseForm
): Promise<ITenantDatabaseResponse> => {
  const res = await request<unknown>({
    options: {
      url: '/tenant_databases',
      method: 'post',
      data: { tenant_database: data }
    }
  });
  return normalizeTenantDatabaseResponse(res);
};

export const updateTenantDatabase = async (
  id: string,
  data: Partial<ITenantDatabaseForm>
): Promise<ITenantDatabaseResponse> => {
  const res = await request<unknown>({
    options: {
      url: `/tenant_databases/${id}`,
      method: 'patch',
      data: { tenant_database: data }
    }
  });
  return normalizeTenantDatabaseResponse(res);
};

export const deleteTenantDatabase = async (id: string): Promise<void> => {
  return request({
    options: {
      url: `/tenant_databases/${id}`,
      method: 'delete'
    }
  });
};

/** Weryfikacja etapami: sieć → baza. Zapisuje wynik w host_diagnostics, zwraca do UI. */
export const verifyTenantDatabase = async (
  id: string
): Promise<IVerifyResponse> => {
  return request({
    options: {
      url: `/tenant_databases/${id}/verify`,
      method: 'post'
    }
  });
};

/** Kolejkuje job migracji (migracje Rails, upsert company, sync zasobów). Wymaga overall_ok z weryfikacji. */
export const migrateTenantDatabase = async (
  id: string
): Promise<IMigrateResponse> => {
  return request({
    options: {
      url: `/tenant_databases/${id}/migrate`,
      method: 'post'
    }
  });
};
